/**
 * Bedridden Patients Dashboard (Option B - Revised)
 *
 * 3 Groups: ติดบ้าน/ติดเตียง, ผู้สูงอายุ, กลุ่มเสี่ยงสูง
 * Map: Polygon with color density (not bubbles)
 * Card 3: Donut chart (not hospital admissions)
 */

import { useState } from 'react'
import {
  BedSingle,
  Shield,
  UserPlus,
  MapPin,
  TrendingUp,
  Activity,
  List,
} from 'lucide-react'
import {
  Sidebar,
  PageHeader,
  YearFilter,
  DistrictFilter,
  BedriddenMapPolygon,
} from '../shared/components'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import { useBedriddenGroups } from '../services/api/bedriddenGroups'
import { useBedriddenDistrictMap, useBedriddenTopDiseases, useBedriddenMonthlyTrends } from '../services/api/bedriddenPatients'
import { useDistricts } from '../services/api/districts'
import { formatNumber } from '../shared/utils/formatNumber'

const COLORS = {
  bedridden: '#2D3561', // korat-navy-700
  elderly: '#F26B3F',   // korat-orange-500
  highRisk: '#ef4444',  // Red (risk color - keep as is)
}

export function BedriddenPatientsDashboard() {
  const [selectedYear, setSelectedYear] = useState(2567)
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null)

  // Fetch data from 3 sources
  const groupsQuery = useBedriddenGroups(
    selectedDistrict ? { district_id: selectedDistrict, year: selectedYear } : { year: selectedYear }
  )
  const { data: districts = [], isLoading: districtsLoading } = useDistricts()

  // Fetch district map data for polygon map
  const districtMapQuery = useBedriddenDistrictMap(
    selectedDistrict ? { district_id: selectedDistrict, year: selectedYear } : { year: selectedYear }
  )

  const data = groupsQuery.data
  const isLoading = groupsQuery.isLoading
  const error = groupsQuery.error

  // Get selected district name for map
  const selectedDistrictName = selectedDistrict
    ? districts.find((d) => d.id === selectedDistrict)?.name_th
    : null

  // Fetch Top Diseases (Real Data)
  const topDiseasesQuery = useBedriddenTopDiseases(
    selectedDistrict ? { district_id: selectedDistrict, year: selectedYear, limit: 5 } : { year: selectedYear, limit: 5 }
  )

  // Fetch Monthly Trends (Real Data)
  const monthlyTrendsQuery = useBedriddenMonthlyTrends(
    selectedDistrict ? { district_id: selectedDistrict, year: selectedYear, months: 6 } : { year: selectedYear, months: 6 }
  )

  // Donut chart data - สัดส่วน 3 กลุ่ม (ข้อมูลจริง)
  const groupDistribution = [
    { name: 'ติดบ้าน/ติดเตียง', value: data?.bedridden.total || 0, color: COLORS.bedridden },
    { name: 'ผู้สูงอายุ', value: data?.elderly.total || 0, color: COLORS.elderly },
    { name: 'กลุ่มเสี่ยงสูง', value: data?.highRisk.total || 0, color: COLORS.highRisk },
  ]

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar currentPage="bedridden" />

      <main className="flex-1 overflow-auto">
        <PageHeader
          icon={BedSingle}
          title="ผู้ป่วยติดบ้าน/ติดเตียง"
          showFilters={true}
          selectedDistrict={selectedDistrictName}
          filters={
            <div className="flex items-center gap-4">
              <button
                onClick={() => (window.location.hash = 'bedridden-patients')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-korat-orange-500 text-white rounded-lg hover:bg-korat-orange-600 transition-colors text-sm font-medium"
              >
                <List className="w-4 h-4" />
                จัดการรายชื่อผู้ป่วย
              </button>
              <YearFilter
                selectedYear={selectedYear}
                onYearChange={setSelectedYear}
                availableYears={[2565, 2566, 2567]}
              />
              <DistrictFilter
                selectedDistrict={selectedDistrict}
                onDistrictChange={setSelectedDistrict}
                districts={districts}
                isLoading={districtsLoading}
              />
            </div>
          }
        />

        <div className="p-6">
          {/* Error State - Only show non-auth errors */}
          {error && !error.message?.includes('401') && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                ⚠️ ไม่สามารถโหลดข้อมูลได้: {error.message || 'กรุณาลองใหม่อีกครั้ง'}
              </p>
            </div>
          )}

          {/* Mock Data Warning */}
          {!data?.bedridden.total || data.bedridden.total === 810 ? (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>💡 โหมดทดสอบ:</strong> ระบบกำลังแสดง Mock Data เพื่อให้เห็น UI ครบทุกองค์ประกอบ
                — ข้อมูลจริงจะมาจาก Backend API หลังจาก Login
              </p>
            </div>
          ) : null}

          {/* Row 1: Map (Left 60%) + KPI Cards (Right 40%) */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">

            {/* Left: Polygon Map with Color Density (3/5 = 60%) */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-korat-navy-700" />
                  <h3 className="text-lg font-semibold text-korat-navy-700">
                    การกระจายผู้ป่วยติดบ้าน/ติดเตียงตามพื้นที่
                  </h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  แผนที่แสดงความหนาแน่นของผู้ป่วยในแต่ละอำเภอด้วยสี
                </p>

                {/* Polygon Map - Real Data */}
                <div className="h-[580px]">
                  <BedriddenMapPolygon
                    districts={districtMapQuery.data}
                    year={selectedYear}
                    selectedDistrictId={selectedDistrict}
                    selectedDistrictName={selectedDistrictName}
                  />
                </div>
              </div>
            </div>

            {/* Right: 3 KPI Cards (2/5 = 40%) */}
            <div className="lg:col-span-2 space-y-4">

              {/* Card 1: Insurance Coverage */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                    <Shield className="w-6 h-6 text-blue-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-slate-600 mb-1">
                      อัตราครอบคลุมสิทธิ์การรักษา
                    </h3>
                    <p className="text-xs text-slate-500 mb-3">
                      สิทธิ์การรักษาพยาบาลครอบคลุมผู้ป่วยติดบ้าน/ติดเตียง
                    </p>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-3xl font-bold text-korat-navy-700 tabular-nums leading-none">98.5</span>
                      <span className="text-lg text-slate-600">%</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">
                      เป้าหมาย ≥95% (WHO)
                    </p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">UC</span>
                        <span className="font-semibold tabular-nums">{formatNumber(data?.bedridden.total || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">SSO</span>
                        <span className="font-semibold tabular-nums">0</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">CSMBS</span>
                        <span className="font-semibold tabular-nums">0</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: New Patients */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center shrink-0">
                    <UserPlus className="w-6 h-6 text-orange-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-slate-600 mb-1">
                      ผู้ป่วยใหม่ในรอบการดูแล
                    </h3>
                    <p className="text-xs text-slate-500 mb-3">
                      จำนวนผู้ป่วยที่เพิ่มเข้ามาในระบบการดูแล
                    </p>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-3xl font-bold text-korat-navy-700 tabular-nums leading-none">
                        {formatNumber(data?.bedridden.total || 362)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-600 font-medium">
                        ▲ 8 ราย เดือนนี้
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Donut Chart - สัดส่วน 3 กลุ่ม */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <h3 className="text-sm font-medium text-slate-600 mb-1">
                  สัดส่วนกลุ่มผู้ป่วย
                </h3>
                <p className="text-xs text-slate-500 mb-4">
                  แบ่งตามประเภทกลุ่มเป้าหมาย
                </p>

                {/* Donut Chart */}
                <div className="flex items-center justify-center">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={groupDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {groupDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => `${formatNumber(value as number)} คน`}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend */}
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.bedridden }}></div>
                      <span className="text-slate-600">ติดบ้าน/ติดเตียง</span>
                    </div>
                    <span className="font-semibold text-korat-navy-700 tabular-nums">
                      {formatNumber(data?.bedridden.total || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.elderly }}></div>
                      <span className="text-slate-600">ผู้สูงอายุ</span>
                    </div>
                    <span className={`font-semibold tabular-nums ${data?.elderly.total ? 'text-korat-navy-700' : 'text-slate-400'}`}>
                      {formatNumber(data?.elderly.total || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.highRisk }}></div>
                      <span className="text-slate-600">กลุ่มเสี่ยงสูง</span>
                    </div>
                    <span className={`font-semibold tabular-nums ${data?.highRisk.total ? 'text-korat-navy-700' : 'text-slate-400'}`}>
                      {formatNumber(data?.highRisk.total || 0)}
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Row 2: Top Diseases Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-korat-navy-700" />
              <h3 className="text-lg font-semibold text-korat-navy-700">
                โรคประจำตัวที่พบบ่อย (Top 5)
              </h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              สถิติการวินิจฉัยโรคประจำตัวในผู้ป่วยติดบ้าน/ติดเตียงแยกตามประเภทโรค
            </p>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">ชื่อโรค</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">จำนวน</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 w-1/2">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {topDiseasesQuery.data.map((disease, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 focus-within:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-700">{idx + 1}. {disease.name}</div>
                        <div className="text-xs text-slate-500">({disease.nameEn})</div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-korat-navy-700 tabular-nums">
                        {formatNumber(disease.count)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-korat-navy-700 rounded-full transition-all"
                              style={{ width: `${disease.percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-korat-navy-700 tabular-nums min-w-[3rem] text-right">
                            {disease.percentage}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                <strong>หมายเหตุ:</strong> ผู้ป่วย 1 คนอาจมีโรคประจำตัวมากกว่า 1 โรค
              </p>
            </div>
          </div>

          {/* Row 3: Monthly Trends Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-korat-navy-700" />
              <h3 className="text-lg font-semibold text-korat-navy-700">
                แนวโน้มใหม่รายเดือน (6 เดือนล่าสุด)
              </h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              จำนวนผู้ป่วยที่เพิ่มเข้ามาในแต่ละเดือนและยอดสะสมในระบบการดูแล
            </p>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">เดือน</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">ใหม่</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">สะสม</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">เปลี่ยนแปลง</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {monthlyTrendsQuery.data.map((trend, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 focus-within:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-slate-700">{trend.month}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                          +{trend.new}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-korat-navy-700 tabular-nums">
                        {formatNumber(trend.total)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-green-600 font-medium">
                          +{trend.change}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>📊 ข้อมูล:</strong> Dashboard นี้แสดงข้อมูลผู้สูงอายุติดบ้านและติดเตียงในจังหวัดนครราชสีมา
              — ข้อมูลเป็นระดับอำเภอ/ตำบล ไม่มีข้อมูลระบุตัวบุคคล (ตาม PDPA)
            </p>
            <p className="text-xs text-blue-600 mt-2">
              💡 <strong>Mock Data:</strong> ข้อมูลปัจจุบันเป็น Mock สำหรับทดสอบ UI — จะอัปเดตเป็นข้อมูลจริงจาก Database ในขั้นตอนถัดไป
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}

export default BedriddenPatientsDashboard
